import type { BodyCharacteristicUI } from "@corpuscomplete/core-ui/body/body-characteristic";
import { BodyCharacteristic } from "@corpuscomplete/core/body/body-characteristic";
import { safeParseInt } from "@corpuscomplete/core/utils";
import { useRef } from "react";
import { Input } from "@corpuscomplete/web-ui/components/input";
import { Text } from "@corpuscomplete/web-ui/components/text";
import { cn } from "@corpuscomplete/web-ui/lib/utils";

/** Segmented DOB input (`MM/DD/YYYY`) with auto-advance/backspace navigation between fields. */
export function DateOfBirthInput({
  value,
  onChange,
  onBlur,
  className,
}: BodyCharacteristicUI.DateOfBirthInputProps) {
  const timeUnitRefs = {
    month: useRef<HTMLInputElement>(null),
    day: useRef<HTMLInputElement>(null),
    year: useRef<HTMLInputElement>(null),
  };
  const maxLengths = {
    month: 2,
    day: 2,
    year: 4,
  };

  const getNextRef = (key: keyof BodyCharacteristic.DateOfBirthForm) => {
    if (key === "month") {
      return timeUnitRefs.day;
    }
    if (key === "day") {
      return timeUnitRefs.year;
    }
    return undefined;
  };
  const getPrevRef = (key: keyof BodyCharacteristic.DateOfBirthForm) => {
    if (key === "year") {
      return timeUnitRefs.day;
    }
    if (key === "day") {
      return timeUnitRefs.month;
    }
    return undefined;
  };

  const handleChange = (timeUnit: keyof BodyCharacteristic.DateOfBirthForm, text: string) => {
    onChange({ ...value, [timeUnit]: text });
    const isNumber = safeParseInt(text) !== null;
    const maxLength = maxLengths[timeUnit];
    if (isNumber && text.length === maxLength) {
      onBlur();
      getNextRef(timeUnit)?.current?.focus();
    }
  };

  const handleKeyDown = (
    timeUnit: keyof BodyCharacteristic.DateOfBirthForm,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    const inputValue = value?.[timeUnit] ?? "";
    if (inputValue === "" && e.key === "Backspace") {
      getPrevRef(timeUnit)?.current?.focus();
    }
  };

  const plainDate = BodyCharacteristic.dateOfBirthFromForm(value);
  const age = plainDate && BodyCharacteristic.dateOfBirthToAge(plainDate).value;

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {/* <Label>
        Date of Birth
        {errors && errors.length > 0 && (
          <span className="ml-2">
            {errors.map((error, i) => (
              <Text key={i} variant="xs" className="text-destructive">{error?.message || String(error)}</Text>
            ))}
          </span>
        )}
      </Label> */}
      <div className="flex items-center gap-2">
        <Input
          maxLength={maxLengths.month}
          ref={timeUnitRefs.month}
          className="w-[60px]"
          type="number"
          inputMode="numeric"
          value={value?.month ?? ""}
          placeholder="MM"
          onChange={(e) => handleChange("month", e.target.value)}
          onKeyDown={(e) => handleKeyDown("month", e)}
          onBlur={onBlur}
        />
        <Text variant="sm">/</Text>
        <Input
          maxLength={maxLengths.day}
          ref={timeUnitRefs.day}
          className="w-[60px]"
          type="number"
          inputMode="numeric"
          value={value?.day ?? ""}
          placeholder="DD"
          onChange={(e) => handleChange("day", e.target.value)}
          onKeyDown={(e) => handleKeyDown("day", e)}
          onBlur={onBlur}
        />
        <Text variant="sm">/</Text>
        <Input
          maxLength={maxLengths.year}
          ref={timeUnitRefs.year}
          className="w-[72px]"
          type="number"
          inputMode="numeric"
          value={value?.year ?? ""}
          placeholder="YYYY"
          onChange={(e) => handleChange("year", e.target.value)}
          onKeyDown={(e) => handleKeyDown("year", e)}
          onBlur={onBlur}
        />
      </div>
      <Text variant="xs" className="text-muted-foreground ml-auto">
        {age ? `${age} years old` : "No age available"}
      </Text>
    </div>
  );
}
